// Chakra imports
import { toastError, presentMutationError, toastWarning } from "../../../utils/showApiErrorToast";
import {
  Flex,
  Text,
  useColorModeValue,
  Box,
  Button,
  SimpleGrid,
  Switch,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import RequireAdmin from "@/components/Access/RequireAdmin";
// Custom components
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDisplayCurrency } from "../../../contexts/DisplayCurrencyContext";
import TradeLogMiniStatistics from "../TradeLog/components/TradeLogMiniStatistics";
import GeographicMap from "./components/GeographicMap";
import RevenueTrendChart from "./components/RevenueTrendChart";
import PaymentMethodChart from "./components/PaymentMethodChart";
import RevenueBySourceChart from "./components/RevenueBySourceChart";
import RecentTransactionsTable from "./components/RecentTransactionsTable";
import NotificationsCenter from "./components/NotificationsCenter";
import MembersTable, { Member } from "./components/MembersTable";
import MemberStatsModal, { MemberStats } from "./components/MemberStatsModal";
import ToastDesigns from "../ToastDesigns";
import { 
  fetchBrokerAccounts, 
  getBrokerCredentials, 
  getBrokerConnectionStatus,
  BrokerAccount,
  fetchSystemAccounts,
  SystemAccount,
  closeAllPositionsForAccount
} from "../../../services/liquidBrokersApi";
import {
  fetchAutomatedTradingStatus,
  enableAutomatedTrading,
  disableAutomatedTrading,
  fetchAutomatedTradingArmsStatus,
  enableAutomatedTradingArm,
  disableAutomatedTradingArm,
  type AutomatedTradingStatus,
  type AutomatedTradingArmsStatus,
  type AutomatedTradingArm,
} from "../../../services/automatedTradingApi";

// Mock data - in production, this would come from an API
const mockUserStats = {
  totalUsers: 1247,
  activeUsers: 892,
  newUsersThisMonth: 156,
  usersWithTrades: 634,
};

// Mock members data - in production, this would come from an API
const mockMembers: Member[] = [
  {
    id: "user_351WCLL640KL5DHksbXPhIVGmH7",
    email: "john.doe@example.com",
    name: "John Doe",
    status: "active",
    subscription: "active",
    portfolioSize: 125000,
    totalPnL: 15234.56,
    totalTrades: 87,
    lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    joinedDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(), // 180 days ago
  },
  {
    id: "user_2a8K9mN3pQr5sT7vWxYzAbCdEfGh",
    email: "jane.smith@example.com",
    name: "Jane Smith",
    status: "active",
    subscription: "active",
    portfolioSize: 89000,
    totalPnL: -3421.23,
    totalTrades: 45,
    lastActive: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
    joinedDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(), // 120 days ago
  },
  {
    id: "user_4b2L8mN9pQr3sT5vWxYzAbCdEfGh",
    email: "bob.wilson@example.com",
    name: "Bob Wilson",
    status: "active",
    subscription: "trial",
    portfolioSize: 45000,
    totalPnL: 5678.90,
    totalTrades: 23,
    lastActive: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
    joinedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
  },
  {
    id: "user_6c3M9nO0pQr2sT4vWxYzAbCdEfGh",
    email: "alice.brown@example.com",
    name: "Alice Brown",
    status: "active",
    subscription: "active",
    portfolioSize: 234000,
    totalPnL: 45678.12,
    totalTrades: 156,
    lastActive: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
    joinedDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(), // 365 days ago
  },
  {
    id: "user_8d4N0oP1qRs3tU5wXyZaBcDeFgH",
    email: "charlie.davis@example.com",
    name: "Charlie Davis",
    status: "inactive",
    subscription: "cancelled",
    portfolioSize: 12000,
    totalPnL: -1234.56,
    totalTrades: 12,
    lastActive: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(), // 15 days ago
    joinedDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days ago
  },
  {
    id: "user_0e5O1pQ2rSt4uV6xYzAbCdEfGhI",
    email: "diana.miller@example.com",
    name: "Diana Miller",
    status: "active",
    subscription: "active",
    portfolioSize: 178000,
    totalPnL: 23456.78,
    totalTrades: 98,
    lastActive: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
    joinedDate: new Date(Date.now() - 240 * 24 * 60 * 60 * 1000).toISOString(), // 240 days ago
  },
  {
    id: "user_2f6P2qR3sTu5vW7yZaBcDeFgHiJ",
    email: "edward.taylor@example.com",
    name: "Edward Taylor",
    status: "active",
    subscription: "active",
    portfolioSize: 67000,
    totalPnL: 8901.23,
    totalTrades: 34,
    lastActive: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
    joinedDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(), // 60 days ago
  },
  {
    id: "user_4g7Q3rS4tUv6wX8zAbCdEfGhIjK",
    email: "fiona.anderson@example.com",
    name: "Fiona Anderson",
    status: "active",
    subscription: "active",
    portfolioSize: 145000,
    totalPnL: -5678.90,
    totalTrades: 67,
    lastActive: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(), // 12 hours ago
    joinedDate: new Date(Date.now() - 200 * 24 * 60 * 60 * 1000).toISOString(), // 200 days ago
  },
];

// Seeded random number generator for consistent values per member
const seededRandom = (seed: number) => {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
};

// Helper function to generate member stats from member data
const generateMemberStats = (member: Member): MemberStats => {
  // Create a seeded random generator based on member ID hash
  const memberIdHash = member.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const random = seededRandom(memberIdHash);
  
  const closedTrades = Math.floor(member.totalTrades * 0.75); // Assume 75% are closed
  const activeTrades = member.totalTrades - closedTrades;
  const winningTrades = Math.floor(closedTrades * 0.55); // Assume 55% win rate
  const losingTrades = closedTrades - winningTrades;
  
  const winRate = closedTrades > 0 ? (winningTrades / closedTrades) * 100 : 0;
  const totalInvested = member.portfolioSize - member.totalPnL;
  const roi = totalInvested > 0 ? Math.round(((member.totalPnL / totalInvested) * 100) * 100) / 100 : 0;
  
  const averageWin = winningTrades > 0 ? (member.totalPnL * 0.6) / winningTrades : 0;
  const averageLoss = losingTrades > 0 ? (member.totalPnL * 0.4) / losingTrades : 0;
  const winLossRatio = averageLoss !== 0 ? averageWin / Math.abs(averageLoss) : 0;
  const profitFactor = averageLoss !== 0 ? (averageWin * winningTrades) / (Math.abs(averageLoss) * losingTrades) : 0;
  
  // Generate recent trades with seeded random
  const recentTrades = Array.from({ length: Math.min(5, closedTrades) }, (_, i) => ({
    id: `trade-${i}`,
    symbol: ["AAPL", "TSLA", "MSFT", "GOOGL", "AMZN", "NVDA", "META", "NFLX"][i % 8],
    date: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString(),
    pnl: (random() - 0.4) * 5000, // Seeded random P&L between -2000 and 3000
    returnPercent: (random() - 0.4) * 20, // Seeded random return between -8% and 12%
  }));

  // Calculate streaks with seeded random
  const currentStreakCount = Math.floor(random() * 5) + 1;
  const longestWinStreak = Math.floor(random() * 10) + 3;
  const longestLossStreak = Math.floor(random() * 5) + 1;
  const sharpeRatio = random() * 2 + 0.5; // Seeded random between 0.5 and 2.5

  return {
    member,
    winRate,
    roi,
    activeTrades,
    closedTrades,
    tradesThisMonth: Math.floor(member.totalTrades * 0.15), // Assume 15% of trades this month
    averageTradeSize: member.portfolioSize / member.totalTrades,
    bestTrade: {
      symbol: "TSLA",
      profit: Math.max(...recentTrades.map(t => t.pnl), 0) || 5000,
      date: recentTrades[0]?.date || new Date().toISOString(),
    },
    worstTrade: {
      symbol: "AAPL",
      loss: Math.min(...recentTrades.map(t => t.pnl), 0) || -2000,
      date: recentTrades[1]?.date || new Date().toISOString(),
    },
    averageWin,
    averageLoss,
    winLossRatio,
    currentStreak: {
      type: member.totalPnL > 0 ? "win" : "loss",
      count: currentStreakCount,
    },
    longestWinStreak,
    longestLossStreak,
    totalInvested,
    totalReturn: member.totalPnL,
    profitFactor,
    sharpeRatio,
    maxDrawdown: -Math.abs(member.totalPnL) * 0.3, // Assume 30% of P&L as max drawdown
    brokerConnected: member.status === "active",
    location: "New York, USA", // Mock location
    accountAge: Math.floor((Date.now() - new Date(member.joinedDate).getTime()) / (1000 * 60 * 60 * 24)),
    recentTrades,
  };
};

// Mock financial data - in production, this would come from an API
// Subscriptions are $50 per month
const subscriptionPrice = 50;
const activeSubscriptions = 850;
const mockFinancialStats = {
  totalRevenue: 510000, // 850 subscriptions * $50 * 12 months average
  monthlyRecurringRevenue: activeSubscriptions * subscriptionPrice, // MRR = 850 * $50 = $42,500
  activeSubscriptions: activeSubscriptions,
  churnRate: 2.3, // ~2.3% monthly churn rate
  averageRevenuePerUser: subscriptionPrice, // ARPU = $50 per month
  newSubscriptionsThisMonth: 45, // New subscriptions this month
  cancelledSubscriptionsThisMonth: Math.round(activeSubscriptions * 0.023), // ~20 cancellations (2.3% churn)
  paymentSuccessRate: 98.5, // 98.5% payment success rate
  totalTransactions: Math.round(activeSubscriptions * 12), // ~10,200 transactions (850 * 12 months average)
  failedPayments: Math.round(activeSubscriptions * 0.015), // ~13 failed payments (1.5% failure rate)
  refundRate: 1.2, // 1.2% refund rate
};

// Interface for portfolio account data
interface PortfolioAccount {
  accountId: string;
  accountName: string;
  portfolioSize: number;
  status: string;
  botEnabled: boolean;
  clerkUserId?: string;
  accountCode?: string;
  error?: string | null;
}

function isAccountConnected(status: string | undefined | null): boolean {
  const s = String(status || "").toLowerCase();
  return s === "connected" || s === "active" || s === "enabled";
}

function mapSystemAccountsToPortfolio(accounts: SystemAccount[] | undefined | null): PortfolioAccount[] {
  const list = Array.isArray(accounts) ? accounts : [];
  return list
    .filter((acc) => Boolean(acc?.username))
    .map((acc) => {
      const portfolioSize =
        acc.portfolio_size == null
          ? 0
          : typeof acc.portfolio_size === "number"
            ? acc.portfolio_size
            : Number(acc.portfolio_size) || 0;
      const status = String(acc.status || "disconnected");
      return {
        accountId: acc.username,
        accountName: acc.username,
        portfolioSize,
        status,
        botEnabled: isAccountConnected(status),
        clerkUserId: acc.clerk_user_id,
        accountCode: acc.account_code,
        error: acc.error ?? null,
      };
    });
}

const Admin: React.FC = () => {
  const { formatMoney } = useDisplayCurrency();
  const textColor = useColorModeValue("gray.700", "white");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const scrollbarThumbBg = useColorModeValue("gray.300", "gray.600");
  const scrollbarThumbHoverBg = useColorModeValue("gray.400", "gray.500");
  
  const [automatedTrading, setAutomatedTrading] = useState<AutomatedTradingStatus | null>(null);
  const [automatedTradingArms, setAutomatedTradingArms] = useState<AutomatedTradingArmsStatus | null>(null);
  const [isTogglingAutomatedTrading, setIsTogglingAutomatedTrading] = useState(false);
  const [isClosingTrades, setIsClosingTrades] = useState(false);
  const [portfolioAccounts, setPortfolioAccounts] = useState<PortfolioAccount[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  // (Deprecated) Previously tracked per-account bot states; now the bot is controlled via global automated-trading endpoints.
  const [closingAccountTrades, setClosingAccountTrades] = useState<Record<string, boolean>>({});
  const [startingAccounts, setStartingAccounts] = useState<Record<string, boolean>>({});
  const [stoppingAccounts, setStoppingAccounts] = useState<Record<string, boolean>>({});
  const [revenueTimeframe, setRevenueTimeframe] = useState<string>("all-time");
  // Load automated trading status on mount and keep a local countdown fresh.
  useEffect(() => {
    let isMounted = true;
    fetchAutomatedTradingStatus()
      .then((s) => {
        if (isMounted) setAutomatedTrading(s);
      })
      .catch(() => null);
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!automatedTrading?.automated_trading_enabled) return;
    const tick = setInterval(() => {
      setAutomatedTrading((prev) => {
        if (!prev) return prev;
        const tr = prev.time_remaining_seconds;
        if (typeof tr !== "number" || !Number.isFinite(tr)) return prev;
        return { ...prev, time_remaining_seconds: Math.max(0, tr - 1) };
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [automatedTrading?.automated_trading_enabled]);

  const refreshAutomatedTradingStatus = useCallback(async () => {
    const s = await fetchAutomatedTradingStatus();
    setAutomatedTrading(s);
  }, []);

  const refreshAutomatedTradingArmsStatus = useCallback(async () => {
    const s = await fetchAutomatedTradingArmsStatus();
    setAutomatedTradingArms(s);
  }, []);

  const automatedTradingEnabled = Boolean(automatedTrading?.automated_trading_enabled);
  const automatedTradingUntil = automatedTrading?.automated_trading_enabled_until ?? null;
  const automatedTradingTimeRemaining = automatedTrading?.time_remaining_seconds ?? null;

  const findArmByAccountCode = useCallback(
    (accountCode?: string): AutomatedTradingArm | null => {
      if (!accountCode) return null;
      const items = automatedTradingArms?.items ?? [];
      return items.find((a) => a.account_code === accountCode) ?? null;
    },
    [automatedTradingArms]
  );

  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const { isOpen: isStatsModalOpen, onOpen: onStatsModalOpen, onClose: onStatsModalClose } = useDisclosure();

  const loadSystemAccounts = useCallback(async () => {
    setIsLoadingAccounts(true);
    try {
      const accounts = await fetchSystemAccounts("liquidbrokers");
      setPortfolioAccounts(mapSystemAccountsToPortfolio(accounts));
    } catch (error: unknown) {
      presentMutationError(error, "Failed to load system accounts");
      setPortfolioAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  }, []);

  // Fetch system accounts on component mount (manual refresh button handles subsequent fetches).
  useEffect(() => {
    void loadSystemAccounts();
  }, [loadSystemAccounts]);

  // Load arm overrides/status once on page load.
  useEffect(() => {
    void refreshAutomatedTradingArmsStatus();
  }, [refreshAutomatedTradingArmsStatus]);

  const handleBotToggle = () => {
    void (async () => {
      setIsTogglingAutomatedTrading(true);
      try {
        const s = automatedTradingEnabled ? await disableAutomatedTrading() : await enableAutomatedTrading();
        setAutomatedTrading(s);
        await refreshAutomatedTradingArmsStatus().catch(() => null);
        s.automated_trading_enabled
          ? toast.success("Automated trading enabled", { description: "The automated trading system is running." })
          : toastWarning("Automated trading halted", { description: "The automated trading system is halted." });
      } catch (error: any) {
        presentMutationError(error, "Failed to update automated trading");
        // Best-effort: refresh status in case backend changed.
        await refreshAutomatedTradingStatus().catch(() => null);
        await refreshAutomatedTradingArmsStatus().catch(() => null);
      } finally {
        setIsTogglingAutomatedTrading(false);
      }
    })();
  };

  const handleCloseAllTrades = async () => {
    setIsClosingTrades(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsClosingTrades(false);
    toast.success("All Trades Closed", { description: "All open trades have been closed on the broker." });
  };

  const handleCloseAccountTrades = async (account: PortfolioAccount) => {
    console.log('🔵 [Admin] Button clicked for:', account.accountName);
    
    if (!account.accountCode) {
      toastError("Cannot close trades", { description: `${account.accountName} does not have an account code configured.` });
      return;
    }

    setClosingAccountTrades(prev => ({ ...prev, [account.accountId]: true }));

    try {
      const result = await closeAllPositionsForAccount(account.accountName, "liquidbrokers");
      const { positions_closed, total_positions, positions_failed } = result;

      if (total_positions === 0) {
        toast.info("No open positions", { description: `No open positions found for ${account.accountName}.` });
      } else {
        toast.success("Positions Closed", {
          description: `Closed ${positions_closed} of ${total_positions} position${total_positions !== 1 ? "s" : ""} for ${account.accountName}${positions_failed > 0 ? ` (${positions_failed} failed)` : ""}.`,
        });
      }

      await loadSystemAccounts();
    } catch (error: unknown) {
      presentMutationError(error, "Failed to close trades");
    } finally {
      setClosingAccountTrades(prev => {
        const newState = { ...prev };
        delete newState[account.accountId];
        return newState;
      });
    }
  };

  const handleToggleAccountBot = async (account: PortfolioAccount) => {
    if (!account.accountCode) return;
    const arm = findArmByAccountCode(account.accountCode);
    const isArmEnabled = Boolean(arm?.enabled);
    if (isArmEnabled) {
      setStoppingAccounts((prev) => ({ ...prev, [account.accountId]: true }));
    } else {
      setStartingAccounts((prev) => ({ ...prev, [account.accountId]: true }));
    }
    try {
      const payload = { account_code: account.accountCode };
      const nextArms = isArmEnabled
        ? await disableAutomatedTradingArm(payload)
        : await enableAutomatedTradingArm(payload);
      setAutomatedTradingArms(nextArms);
      isArmEnabled
        ? toastWarning("Arm disabled", { description: `${account.accountName} (${account.accountCode})` })
        : toast.success("Arm enabled", { description: `${account.accountName} (${account.accountCode})` });
    } catch (error: any) {
      presentMutationError(error, "Failed to update arm status");
      await refreshAutomatedTradingArmsStatus().catch(() => null);
    } finally {
      if (isArmEnabled) {
        setStoppingAccounts((prev) => {
          const next = { ...prev };
          delete next[account.accountId];
          return next;
        });
      } else {
        setStartingAccounts((prev) => {
          const next = { ...prev };
          delete next[account.accountId];
          return next;
        });
      }
    }
  };

  // No need to group accounts - we show all model accounts in a single table

  const paddingTop = { base: "120px", md: "100px", lg: "60px" };

  return (
    <RequireAdmin>
    <Flex 
      direction="column" 
      pt={paddingTop} 
      pb={8}
    >
      <PageRouteHeadline mb="24px">Admin</PageRouteHeadline>

      {/* Tabs */}
      <Tabs colorScheme="cyan" mb="24px">
        <Box
          overflowX="auto"
          overflowY="hidden"
          w="100%"
          sx={{
            "&::-webkit-scrollbar": {
              height: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: scrollbarThumbBg,
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: scrollbarThumbHoverBg,
            },
          }}
        >
          <TabList 
            borderBottom="2px solid" 
            borderColor={useColorModeValue("gray.200", "gray.600")} 
            mb="24px"
            display="flex"
            flexWrap="nowrap"
            minW="max-content"
          >
            <Tab
              fontSize="md"
              fontWeight="medium"
              color={useColorModeValue("gray.600", "gray.400")}
              _selected={{
                color: "#38BBD1",
                borderBottom: "2px solid",
                borderColor: "#38BBD1",
              }}
              fontFamily="'IBM Plex Sans', sans-serif"
              px={6}
              py={3}
              flexShrink={0}
              whiteSpace="nowrap"
            >
              Bot
            </Tab>
            <Tab
              fontSize="md"
              fontWeight="medium"
              color={useColorModeValue("gray.600", "gray.400")}
              _selected={{
                color: "#38BBD1",
                borderBottom: "2px solid",
                borderColor: "#38BBD1",
              }}
              fontFamily="'IBM Plex Sans', sans-serif"
              px={6}
              py={3}
              flexShrink={0}
              whiteSpace="nowrap"
            >
              Members
            </Tab>
            <Tab
              fontSize="md"
              fontWeight="medium"
              color={useColorModeValue("gray.600", "gray.400")}
              _selected={{
                color: "#38BBD1",
                borderBottom: "2px solid",
                borderColor: "#38BBD1",
              }}
              fontFamily="'IBM Plex Sans', sans-serif"
              px={6}
              py={3}
              flexShrink={0}
              whiteSpace="nowrap"
            >
              Financials
            </Tab>
            <Tab
              fontSize="md"
              fontWeight="medium"
              color={useColorModeValue("gray.600", "gray.400")}
              _selected={{
                color: "#38BBD1",
                borderBottom: "2px solid",
                borderColor: "#38BBD1",
              }}
              fontFamily="'IBM Plex Sans', sans-serif"
              px={6}
              py={3}
              flexShrink={0}
              whiteSpace="nowrap"
            >
              Notifications
            </Tab>
            <Tab
              fontSize="md"
              fontWeight="medium"
              color={useColorModeValue("gray.600", "gray.400")}
              _selected={{
                color: "#38BBD1",
                borderBottom: "2px solid",
                borderColor: "#38BBD1",
              }}
              fontFamily="'IBM Plex Sans', sans-serif"
              px={6}
              py={3}
              flexShrink={0}
              whiteSpace="nowrap"
            >
              UI Testing
            </Tab>
          </TabList>
        </Box>

        <TabPanels>
          {/* Bot Tab */}
          <TabPanel px={0}>
            {/* Bot System Controls */}
            <Card bg={cardBg} mb={6} borderRadius="lg" boxShadow="sm">
              <CardHeader pb={4}>
                <Text
                  fontSize="sm"
                  color="gray.400"
                  fontWeight="medium"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  textTransform="uppercase"
                >
                  Bot System Controls
                </Text>
              </CardHeader>
              <CardBody pt={0} px={6} pb={6}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl>
                    <Flex align="center" justify="space-between" w="100%">
                      <Box flex={1}>
                        <FormLabel 
                          mb={2} 
                          color={textColor} 
                          fontFamily="'IBM Plex Sans', sans-serif" 
                          fontWeight="medium"
                          fontSize="sm"
                        >
                          Trading Bot Status
                        </FormLabel>
                        <Text 
                          fontSize="xs" 
                          color="gray.500" 
                          fontFamily="'IBM Plex Sans', sans-serif"
                          lineHeight="1.4"
                        >
                          Enable or disable the automated trading bot system
                        </Text>
                      </Box>
                      <Switch
                        isChecked={automatedTradingEnabled}
                        onChange={handleBotToggle}
                        colorScheme="green"
                        size="lg"
                        ml={6}
                        flexShrink={0}
                        isDisabled={isTogglingAutomatedTrading}
                      />
                    </Flex>
                  </FormControl>

                  <Box>
                    <VStack align="stretch" spacing={3}>
                      <Button
                        bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                        color="white"
                        size="md"
                        onClick={handleCloseAllTrades}
                        isLoading={isClosingTrades}
                        loadingText="Closing Trades..."
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                        textTransform="uppercase"
                        fontSize="xs"
                        w="fit-content"
                        _hover={{
                          opacity: 0.9
                        }}
                      >
                        Close All Trades on Broker
                      </Button>
                      <Text 
                        fontSize="xs" 
                        color="gray.500" 
                        fontFamily="'IBM Plex Sans', sans-serif"
                        lineHeight="1.4"
                      >
                        This will close all open trades across all accounts on the broker. This action cannot be undone.
                      </Text>
                    </VStack>
                  </Box>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* Account Control Center */}
            <Card bg={cardBg} mb={6} borderRadius="lg" boxShadow="sm" w="100%" maxW="100%">
              <CardHeader pb={4}>
                <Flex align="center" justify="space-between" gap={3}>
                  <Text
                    fontSize="sm"
                    color="gray.400"
                    fontWeight="medium"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    textTransform="uppercase"
                  >
                    Account Control Center
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => void loadSystemAccounts()}
                    isLoading={isLoadingAccounts}
                    loadingText="Refreshing..."
                    fontFamily="'IBM Plex Sans', sans-serif"
                    textTransform="uppercase"
                  >
                    Refresh
                  </Button>
                </Flex>
              </CardHeader>
              <CardBody pt={0} px={0} pb={6} w="100%">
                {isLoadingAccounts ? (
                  <Flex justify="center" align="center" py={8}>
                    <Text color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
                      Loading accounts...
                    </Text>
                  </Flex>
                ) : portfolioAccounts.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" py={8} px={6}>
                    <Text 
                      color={textColor} 
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontSize="sm"
                      textAlign="center"
                      mb={2}
                    >
                      No system accounts found
                    </Text>
                    <Text 
                      color="gray.500" 
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontSize="xs"
                      textAlign="center"
                    >
                      Click refresh to fetch latest system accounts.
                    </Text>
                  </Flex>
                ) : (
                  <Box px={6} w="100%">
                    <Box overflowX="auto" w="100%" maxW="100%">
                      <Table variant="simple" colorScheme="gray" size="sm" w="100%">
                        <Thead>
                          <Tr>
                            <Th 
                              fontFamily="'IBM Plex Sans', sans-serif" 
                              fontWeight="medium" 
                              color="gray.400"
                              textTransform="uppercase"
                              fontSize="xs"
                              letterSpacing="0.5px"
                              pb={3}
                            >
                              Account Name
                            </Th>
                            <Th 
                              fontFamily="'IBM Plex Sans', sans-serif" 
                              fontWeight="medium" 
                              color="gray.400"
                              textTransform="uppercase"
                              fontSize="xs"
                              letterSpacing="0.5px"
                              pb={3}
                            >
                              Account Code
                            </Th>
                            <Th 
                              isNumeric 
                              fontFamily="'IBM Plex Sans', sans-serif" 
                              fontWeight="medium" 
                              color="gray.400"
                              textTransform="uppercase"
                              fontSize="xs"
                              letterSpacing="0.5px"
                              pb={3}
                            >
                              Portfolio Size
                            </Th>
                            <Th 
                              fontFamily="'IBM Plex Sans', sans-serif" 
                              fontWeight="medium" 
                              color="gray.400"
                              textTransform="uppercase"
                              fontSize="xs"
                              letterSpacing="0.5px"
                              pb={3}
                            >
                              Status
                            </Th>
                            <Th 
                              fontFamily="'IBM Plex Sans', sans-serif" 
                              fontWeight="medium" 
                              color="gray.400"
                              textTransform="uppercase"
                              fontSize="xs"
                              letterSpacing="0.5px"
                              pb={3}
                            >
                              Bot Status
                            </Th>
                            <Th 
                              fontFamily="'IBM Plex Sans', sans-serif" 
                              fontWeight="medium" 
                              color="gray.400"
                              textTransform="uppercase"
                              fontSize="xs"
                              letterSpacing="0.5px"
                              pb={3}
                              textAlign="center"
                            >
                              Bot Control
                            </Th>
                            <Th 
                              fontFamily="'IBM Plex Sans', sans-serif" 
                              fontWeight="medium" 
                              color="gray.400"
                              textTransform="uppercase"
                              fontSize="xs"
                              letterSpacing="0.5px"
                              pb={3}
                              textAlign="center"
                            >
                              Close Trades
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {portfolioAccounts.length === 0 ? (
                            <Tr>
                              <Td colSpan={7} textAlign="center" py={8}>
                                <Text color={textColor} fontFamily="'IBM Plex Sans', sans-serif">
                                  No accounts to display
                                </Text>
                              </Td>
                            </Tr>
                          ) : (
                            portfolioAccounts.map((account) => (
                            <Tr key={account.accountId} _hover={{ bg: rowHoverBg }} transition="background-color 0.2s">
                              <Td 
                                fontFamily="'IBM Plex Sans', sans-serif" 
                                color={textColor}
                                py={3}
                                fontWeight="medium"
                              >
                                {account.accountName}
                              </Td>
                              <Td 
                                fontFamily="'IBM Plex Sans', sans-serif" 
                                color={textColor}
                                py={3}
                                fontSize="sm"
                              >
                                {account.accountCode || (
                                  <Text as="span" color="gray.500" fontStyle="italic">
                                    N/A
                                  </Text>
                                )}
                              </Td>
                              <Td 
                                isNumeric 
                                fontFamily="'IBM Plex Sans', sans-serif" 
                                color={textColor} 
                                fontWeight="medium"
                                py={3}
                              >
                                {(() => {
                                  const size = account.portfolioSize ?? 0;
                                  return formatMoney(size);
                                })()}
                              </Td>
                              <Td py={3}>
                                <Badge
                                  colorScheme={isAccountConnected(account.status) ? "green" : "gray"}
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  fontSize="xs"
                                  px={2}
                                  py={1}
                                  borderRadius="full"
                                  fontWeight="medium"
                                >
                                  {isAccountConnected(account.status) ? "Connected" : "Disconnected"}
                                </Badge>
                              </Td>
                              <Td py={3}>
                                <Badge
                                  colorScheme={(findArmByAccountCode(account.accountCode)?.enabled ?? false) ? "green" : "gray"}
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  fontSize="xs"
                                  px={2}
                                  py={1}
                                  borderRadius="full"
                                  fontWeight="medium"
                                >
                                  {findArmByAccountCode(account.accountCode)?.enabled ? "Enabled" : "Disabled"}
                                </Badge>
                              </Td>
                              <Td py={3} textAlign="center">
                                <Button
                                  size="xs"
                                  bgGradient={(findArmByAccountCode(account.accountCode)?.enabled ?? false)
                                    ? "linear-gradient(135deg, #E00101, #FE4244)" 
                                    : "linear-gradient(135deg, #06E363, #AAFAB1)"}
                                  color="white"
                                  onClick={() => handleToggleAccountBot(account)}
                                  isLoading={startingAccounts[account.accountId] || stoppingAccounts[account.accountId]}
                                  loadingText={(findArmByAccountCode(account.accountCode)?.enabled ?? false) ? "Disabling..." : "Enabling..."}
                                  isDisabled={!account.accountCode}
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  fontWeight="medium"
                                  textTransform="uppercase"
                                  fontSize="10px"
                                  w="100%"
                                  maxW="120px"
                                  _hover={{
                                    opacity: 0.9
                                  }}
                                  _disabled={{
                                    opacity: 0.5,
                                    cursor: "not-allowed"
                                  }}
                                >
                                  {(findArmByAccountCode(account.accountCode)?.enabled ?? false) ? "Stop Bot" : "Start Bot"}
                                </Button>
                              </Td>
                              <Td py={3} textAlign="center">
                                <Button
                                  size="xs"
                                  bgGradient="linear-gradient(135deg, #E00101, #FE4244)"
                                  color="white"
                                  onClick={() => {
                                    console.log('🔵 [Admin] Button onClick triggered for:', account.accountName);
                                    handleCloseAccountTrades(account);
                                  }}
                                  isDisabled={!account.accountCode}
                                  _disabled={{
                                    opacity: 0.5,
                                    cursor: "not-allowed"
                                  }}
                                  isLoading={closingAccountTrades[account.accountId]}
                                  loadingText="Closing..."
                                  fontFamily="'IBM Plex Sans', sans-serif"
                                  fontWeight="medium"
                                  textTransform="uppercase"
                                  fontSize="10px"
                                  w="100%"
                                  maxW="120px"
                                  _hover={{
                                    opacity: 0.9
                                  }}
                                >
                                  Close Trades
                                </Button>
                              </Td>
                            </Tr>
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Members Tab */}
          <TabPanel px={0}>
            {/* User Statistics */}
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={6} mb={6}>
              <TradeLogMiniStatistics
                title="Total Users"
                amount={mockUserStats.totalUsers.toLocaleString()}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="Active Users"
                amount={mockUserStats.activeUsers.toLocaleString()}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="New Users (Month)"
                amount={mockUserStats.newUsersThisMonth.toLocaleString()}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="Users with Trades"
                amount={mockUserStats.usersWithTrades.toLocaleString()}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                showPercentage={false}
              />
            </SimpleGrid>

           {/* Geographic Map Card */}
           <Box mt={6} mb={6}>
             <GeographicMap height="500px" />
           </Box>

           {/* Members Table */}
           <Box mt={6}>
             <MembersTable 
               members={mockMembers} 
               onViewStats={(member) => {
                 setSelectedMember(member);
                 onStatsModalOpen();
               }}
             />
           </Box>

           {/* Member Stats Modal */}
           <MemberStatsModal
             isOpen={isStatsModalOpen}
             onClose={() => {
               onStatsModalClose();
               setSelectedMember(null);
             }}
             stats={selectedMember ? generateMemberStats(selectedMember) : null}
           />
          </TabPanel>

          {/* Financials Tab */}
          <TabPanel px={0}>
            {/* Financial Statistics */}
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={6} mb={6}>
              <TradeLogMiniStatistics
                title="Total Revenue"
                amount={formatMoney(mockFinancialStats.totalRevenue)}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="MRR"
                amount={formatMoney(mockFinancialStats.monthlyRecurringRevenue)}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="Active Subscriptions"
                amount={mockFinancialStats.activeSubscriptions.toLocaleString()}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="Churn Rate"
                amount={`${mockFinancialStats.churnRate}%`}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #E00101, #FE4244)"
                showPercentage={false}
              />
            </SimpleGrid>

            {/* Additional Financial Metrics */}
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={6} mb={6}>
              <TradeLogMiniStatistics
                title="ARPU"
                amount={formatMoney(mockFinancialStats.averageRevenuePerUser)}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="New Subscriptions (This Month)"
                amount={mockFinancialStats.newSubscriptionsThisMonth.toLocaleString()}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="Payment Success Rate"
                amount={`${mockFinancialStats.paymentSuccessRate}%`}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                showPercentage={false}
              />
              <TradeLogMiniStatistics
                title="Total Transactions"
                amount={mockFinancialStats.totalTransactions.toLocaleString()}
                percentage={0}
                backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                showPercentage={false}
              />
            </SimpleGrid>

            {/* Revenue Trend Chart */}
            <Box mb={6}>
              <RevenueTrendChart 
                timeframe={revenueTimeframe}
                onTimeframeChange={setRevenueTimeframe}
              />
            </Box>

            {/* Payment Method Distribution and Revenue by Source */}
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={6}>
              <Card bg={cardBg} borderRadius="lg" boxShadow="sm">
                <CardHeader pb={4}>
                  <Text
                    fontSize="sm"
                    color="gray.400"
                    fontWeight="medium"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    textTransform="uppercase"
                  >
                    Payment Methods
                  </Text>
                </CardHeader>
                <CardBody display="flex" alignItems="center" justifyContent="center" p={4}>
                  <PaymentMethodChart />
                </CardBody>
              </Card>

              <Card bg={cardBg} borderRadius="lg" boxShadow="sm">
                <CardHeader pb={4}>
                  <Text
                    fontSize="sm"
                    color="gray.400"
                    fontWeight="medium"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    textTransform="uppercase"
                  >
                    Revenue by Source
                  </Text>
                </CardHeader>
                <CardBody>
                  <RevenueBySourceChart />
                </CardBody>
              </Card>

              {/* Third card placeholder - add your content here */}
              <Card bg={cardBg} borderRadius="lg" boxShadow="sm">
                <CardHeader pb={4}>
                  <Text
                    fontSize="sm"
                    color="gray.400"
                    fontWeight="medium"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    textTransform="uppercase"
                  >
                    Placeholder
                  </Text>
                </CardHeader>
                <CardBody>
                  <Text color={textColor}>Add content here</Text>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Recent Transactions Table */}
            <Card bg={cardBg} mb={6} borderRadius="lg" boxShadow="sm">
              <CardHeader pb={4}>
                <Text
                  fontSize="sm"
                  color="gray.400"
                  fontWeight="medium"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  textTransform="uppercase"
                >
                  Recent Transactions
                </Text>
              </CardHeader>
              <CardBody>
                <RecentTransactionsTable />
              </CardBody>
            </Card>
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel px={0}>
            <NotificationsCenter 
              totalUsers={mockUserStats.totalUsers}
              activeUsers={mockUserStats.activeUsers}
              inactiveUsers={mockUserStats.totalUsers - mockUserStats.activeUsers}
            />
          </TabPanel>

          {/* UI Testing Tab */}
          <TabPanel px={0}>
            <ToastDesigns />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
    </RequireAdmin>
  );
};

export default Admin;

